import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { partnerApi, type Partner } from '../../lib/api';
import { AiToolsTabs } from '../ai-tools/AiToolsTabs';
import styles from './SkillsPage.module.css';

const SKILLS_COPY = {
  description: 'Teach agents when to use your tools and how they work together.',
  editorLabel: 'Skills & workflows',
  placeholder:
    'When a caller asks to book an appointment, first look them up with your lookup tool, then create the booking, then read the confirmed time back to them…',
  tipsHeading: 'What to write',
  savedNote: "Saved. Every tenant's AI phone agent is being updated with your changes.",
  errorNote: 'Your changes could not be saved. Try again.',
  loadErrorNote: 'Your skills could not be loaded. Refresh the page to try again.',
  saveLabel: 'Save skills',
  savingLabel: 'Saving…',
} as const;

const SKILLS_TIPS = [
  'Refer to tools by the exact name they have on the Tools tab.',
  'For each tool, say when the agent should use it and what to ask the caller first.',
  'Write multi-step workflows as ordered steps: look up the caller, then book, then confirm.',
  'Say what the agent should do when a tool fails or finds nothing.',
] as const;

export const SkillsPage = () => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);

  const { data: partner, isLoading } = useQuery<Partner>({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const saveSkills = useMutation({
    // Re-fetch at save time so the whole-object write merges over current settings, not a stale cache.
    mutationFn: async (skills: string) => {
      const freshPartner = await partnerApi.getPartner();
      const savedCustomizations = freshPartner.featureCustomizations ?? {};
      return partnerApi.updatePartnerProfile({
        featureCustomizations: {
          ...savedCustomizations,
          voice: {
            ...savedCustomizations.voice,
            informationCollectionPrompt: skills,
          },
        },
      });
    },
    onSuccess: () => {
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ['partner'] });
    },
  });

  const savedSkills = partner?.featureCustomizations?.voice?.informationCollectionPrompt ?? '';
  const skillsDraft = draft ?? savedSkills;
  const draftIsUnsaved = skillsDraft !== savedSkills;
  const showSavedNote = saveSkills.isSuccess && !draftIsUnsaved;
  const saveLabel = saveSkills.isPending ? SKILLS_COPY.savingLabel : SKILLS_COPY.saveLabel;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    saveSkills.mutate(skillsDraft);
  };

  return (
    <div className={styles.page}>
      <AiToolsTabs active="skills" />
      <PageHeader title="Skills" description={SKILLS_COPY.description} />

      {isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : !partner ? (
        <p className={styles.error}>{SKILLS_COPY.loadErrorNote}</p>
      ) : (
        <form className={styles.editor} onSubmit={handleSubmit}>
          <label htmlFor="agent-skills">{SKILLS_COPY.editorLabel}</label>
          <textarea
            id="agent-skills"
            rows={16}
            value={skillsDraft}
            onChange={event => setDraft(event.target.value)}
            placeholder={SKILLS_COPY.placeholder}
          />

          <section className={styles.tips}>
            <h2>{SKILLS_COPY.tipsHeading}</h2>
            <ul>
              {SKILLS_TIPS.map(tip => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </section>

          <footer className={styles.editorFooter}>
            {saveSkills.isError && <p className={styles.error}>{SKILLS_COPY.errorNote}</p>}
            {showSavedNote && <p className={styles.saved}>{SKILLS_COPY.savedNote}</p>}
            <button type="submit" disabled={!draftIsUnsaved || saveSkills.isPending}>
              {saveLabel}
            </button>
          </footer>
        </form>
      )}
    </div>
  );
};
